import {Popover, Transition} from '@headlessui/react'
import classNames from 'classnames'
import React, {Fragment} from 'react'

export interface FlyoutMenuItemProps {
  className?: string
  name: string
  description?: string
  href: string
  onClick?: () => void
}

export const FlyoutMenuItem: React.FC<FlyoutMenuItemProps> = ({
  className,
  href,
  name,
  onClick,
  description,
}) => (
  <a
    className={classNames(
      'flex items-start py-3 pl-4 pr-2 -m-3 rounded-sm border-2 border-new-white transition ease-in-out duration-150 bg-gray-100 active:bg-gray-200 hover:border-gray-800 active:border-gray-800 text-gray-800 hover:no-underline',
      className
    )}
    href={href}
    onClick={() => {
      if (onClick) onClick()
    }}
  >
    <p className="leading-tight">{name}</p>
    {description && <p className="mt-1 text-base leading-tight">{description}</p>}
  </a>
)

export interface FlyoutMenuProps {
  children?: React.ReactNode
  menuTitle: string
  className?: string
  buttonClassName?: string
  isHero?: boolean
}

export default function FlyoutMenu(props: FlyoutMenuProps): React.ReactElement {
  const { isHero = false } = props;
  
  return (
    <Popover className={props.className}>
      <>
        <Popover.Button
          className={classNames(
            // Using a similar structure to the plain nav links:
            'group bg-transparent rounded inline-flex items-center',
            props.buttonClassName || 'p-3'
          )}
        >
          <span className="relative inline-flex items-center">
            {/* Growing blue circle background */}
            {isHero && (
              <span
                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-0 h-0 rounded-full bg-new-teal transition-all duration-300 ease-out group-hover:w-16 group-hover:h-16"
              />
            )}
            {/* Menu trigger text */}
            <span className="relative z-10 transition-transform duration-300 ease-out group-hover:scale-110 uppercase tracking-widest font-medium">
              {props.menuTitle}
            </span>
          </span>
          {/* Caret Icon */}
          <svg
            aria-hidden="true"
            className="h-6 ml-1 text-new-white group-hover:text-gray-300"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              fillRule="evenodd"
            />
          </svg>
        </Popover.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel className="absolute z-10 mt-2 w-screen px-0 max-w-screen-xxs top-full">
            <div className="overflow-hidden">
              {/* Use a background color that works with our flyover link styles */}
              <div className="relative p-6 grid gap-6 sm:gap-8">
                {props.children}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </>
    </Popover>
  )
}
